import { FC, Fragment, useMemo } from 'react';

import { DropzoneState, useDropzone } from 'react-dropzone';
import { UploadProps } from '../../../utils/models';
import { concatClassNames } from '../../../utils/utilities';

import Conditional from '../../common/conditional';
import Loader from '../loader';
import UploadPreview from './uploadPreview';

import css from './upload.module.scss';

type ButtonProps = {
	role: string;
	className: string;
	'aria-label': string;
};

const UploadWithPreview: FC<UploadProps> = (props: UploadProps) => {
	const dropzone: DropzoneState = useDropzone(props);

	const buttonProps = useMemo((): ButtonProps => {
		return {
			role: 'button',
			'aria-label': 'drag and drop area',
			className: css['file-upload'],
		};
	}, []);

	const inputProps = useMemo(() => {
		return { className: 'form-input-group-field' };
	}, []);

	const fileLabel = useMemo((): string => {
		if (dropzone.isDragActive) return 'Drop here';

		const hasFiles: boolean = props.files.length > 0;
		const fileName: string = props.files.map((f) => f.caption).join(', ');

		return hasFiles ? fileName : 'Drag and drop files here';
	}, [dropzone.isDragActive, props.files]);

	return (
		<Fragment>
			<Loader isVisible={props.loading as boolean} />

			<div {...dropzone.getRootProps(buttonProps)}>
				<input {...dropzone.getInputProps(inputProps)} />

				<div className={css['file-upload-label']}>{props.label}</div>

				<div className={css['file-upload-body']}>
					{props.sublabel} (Max {props.max} file(s))
				</div>

				<button className={css.uploadButton} type='button'>
					<i className="bi bi-upload"></i> Choose File
				</button>

				<span className={css['file-upload-sublabel']}>{fileLabel}</span>

				{props.error && <div className="error">{props.error}</div>}
			</div>

			<UploadPreview
				hideCaption={props.hideCaption}
				onChangeFiles={props.onFilesChange}
				files={props.files}
				onRemoveFile={props.onFileRemove}
				onChangeFile={props.onFileEdit}
			/>

			<Conditional isVisible={props.files.length > 0 && props.files.length < props.max}>
				<div className={concatClassNames([css['file-upload'], 'mt-2'])}>
					<button className={concatClassNames([css.uploadButton, css.uploadButtonDark])} onClick={dropzone.open} type='button'>
						<i className="bi bi-clipboard-plus-fill"></i> Click to Add Another Image
					</button>
				</div>
			</Conditional>
		</Fragment>
	);
};

UploadWithPreview.defaultProps = {
	max: 1
}

export default UploadWithPreview;
